"use client";

import * as React from "react";
import {
  useWalletPortfolioContext,
  usePortfolioFiat,
} from "../../providers/wallet-portfolio-provider";
import { useStakeInfo } from "../../queries/use-stake-info";
import { useStakeLatestReward } from "../../queries/use-stake-rewards";
import { useStakingOnchain } from "../../queries/use-staking-onchain";
import { useCrossUsdPrice } from "../../queries/use-cross-usd-price";
import { APP_LAUNCHER_ICONS_BASE, STAKING_WEB } from "../../constants";
import { formatPortfolioDecimalAmount, formatPortfolioUsd } from "../../utils";
import { SectionCard } from "./SectionCard";
import { ExternalLinkIcon } from "../icons";
import { handleOutlinkClick } from "../../../../lib/outlink";
import BigNumber from "bignumber.js";

const SECTION_TITLE = "CROSS Staking";

function formatApr(apr: string | undefined): string {
  if (!apr) return "—";
  const value = new BigNumber(apr);
  if (!value.isFinite()) return "—";
  return `${value.toFormat(2, BigNumber.ROUND_DOWN)}%`;
}

export function StakeSection() {
  const { env, walletAddress, onOutlink, stakingRewardsReader } =
    useWalletPortfolioContext();
  const fiat = usePortfolioFiat();
  const hasAddress = !!walletAddress;
  const { stakeInfo, isLoading: stakeLoading } = useStakeInfo(
    env,
    walletAddress,
    hasAddress,
  );
  const {
    latestReward,
    aprAccrued,
    isLoading: rewardLoading,
  } = useStakeLatestReward(env, walletAddress, hasAddress);
  // 온체인 earned()/balanceOf() — 주입형 reader가 있을 때만. 미주입 시 undefined.
  const { onchain } = useStakingOnchain(
    walletAddress,
    stakingRewardsReader,
    hasAddress,
  );

  // CROSS/USD 단가 — Staked / Rewards Earned 금액 아래 $환산가 표기에 사용.
  const crossUsdPrice = useCrossUsdPrice(env, walletAddress, hasAddress);

  const sectionIcon = `${APP_LAUNCHER_ICONS_BASE[env]}/cross-staking.png`;
  const manageUrl = STAKING_WEB[env];

  const totalStake = stakeInfo?.total_stake ?? "0";

  // Rewards Earned: posa 포트폴리오와 동일하게 온체인 earned()(미청구 누적
  // 리워드, raw wei)를 우선 표기한다. reader 미주입 시 API cumulative_earned
  // (없으면 stake 요약 rewards_earned)로 폴백. 표기 직전에만 floor 포맷한다.
  const rewardsEarned = React.useMemo(() => {
    if (onchain) {
      return new BigNumber(onchain.earnedWei.toString())
        .shiftedBy(-18)
        .toString(10);
    }
    return latestReward?.cumulative_earned ?? stakeInfo?.rewards_earned ?? "0";
  }, [onchain, latestReward, stakeInfo]);

  // 개인 실현 APR (posa 포트폴리오와 동일 공식):
  // (차트 누적 리워드 / 원금) × (365 / 경과일) × 100. 첫날(경과일 0)은 1일로 클램프.
  // - 분자: pickChartAccrued(aprAccrued) — 오늘(없으면 최신일) 버킷 cumulative_earned.
  //   변동이 큰 온체인 earned() 대신 안정적인 차트 누적값을 써 posa와 동일하게 맞춘다.
  // - 분모: 온체인 balanceOf(stakedWei) 우선, reader 미주입 시 API total_stake.
  const principal = React.useMemo(
    () =>
      onchain
        ? new BigNumber(onchain.stakedWei.toString()).shiftedBy(-18)
        : new BigNumber(totalStake),
    [onchain, totalStake],
  );
  const accrued =
    aprAccrued !== undefined ? new BigNumber(aprAccrued) : undefined;
  const daysSinceFirstStake = stakeInfo?.days_since_first_stake;
  const myApr =
    accrued !== undefined &&
    accrued.isFinite() &&
    principal.isGreaterThan(0) &&
    daysSinceFirstStake !== null &&
    daysSinceFirstStake !== undefined
      ? accrued
          .div(principal)
          .times(365)
          .div(Math.max(1, daysSinceFirstStake))
          .times(100)
      : undefined;

  // 스테이킹 잔액과 누적 리워드가 모두 0이면 미스테이킹 상태 —
  // 보유 자산이 없는 카테고리이므로 섹션 카드 자체를 숨긴다.
  const hasStake = !(
    new BigNumber(totalStake).isZero() && new BigNumber(rewardsEarned).isZero()
  );

  // totalStake / rewardsEarned는 이미 CROSS 십진 문자열이므로 decimals=0.
  // 단가를 못 구하면(null) 환산가 줄을 숨긴다.
  const stakedUsd = formatPortfolioUsd(totalStake, 0, crossUsdPrice, fiat);
  const rewardsEarnedUsd = formatPortfolioUsd(
    rewardsEarned,
    0,
    crossUsdPrice,
    fiat,
  );

  return (
    <SectionCard
      sectionKey="staking"
      title={SECTION_TITLE}
      icon={sectionIcon}
      iconFit="contain"
      isEmpty={!hasStake}
      loading={stakeLoading || rewardLoading}
    >
      <article className="wp-item" data-side="stake">
        <div className="wp-item-body">
          <div className="wp-item-line">
            <span className="wp-item-label">Staked</span>
            <div className="wp-svc-amount">
              <span className="wp-svc-amount-main">
                {formatPortfolioDecimalAmount(totalStake)} CROSS
              </span>
              {stakedUsd && <span className="wp-item-usd">{stakedUsd}</span>}
            </div>
          </div>
          <div className="wp-item-line">
            <span className="wp-item-label">Rewards Earned</span>
            <div className="wp-svc-amount">
              <span className="wp-svc-amount-main">
                {formatPortfolioDecimalAmount(rewardsEarned)} CROSS
              </span>
              {rewardsEarnedUsd && (
                <span className="wp-item-usd">{rewardsEarnedUsd}</span>
              )}
            </div>
          </div>
          <div className="wp-item-line">
            <span className="wp-item-label">My APR</span>
            <span>{formatApr(myApr?.toString(10))}</span>
          </div>
        </div>
        <div className="wp-item-tags">
          <a
            className="wp-tag wp-tag--mint"
            href={manageUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-track="stake_outlink"
            onClick={(e) =>
              handleOutlinkClick(
                e,
                manageUrl,
                {
                  category: "portfolio",
                  origin: "portfolio-stake",
                  payload: { stakeInfo },
                },
                onOutlink,
              )
            }
          >
            <span className="wp-tag-label">Manage</span>
            <ExternalLinkIcon size={12} className="wp-tag-icon" />
          </a>
        </div>
      </article>
    </SectionCard>
  );
}
